import { defineStore, storeToRefs } from 'pinia';
import { getQuarter, toFileNameFormat } from '@/helpers/date';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useFilters } from '@/stores/filters/filters';
import { useDatePicker } from '@/stores/filters/datePicker';
import { format } from 'date-fns';
import { isEmpty, kebabCase } from 'lodash-es';
import { ref, computed } from 'vue';
import {
  getDemographicSurveyReport,
  loadReqFillRate,
  downloadRequisitionFillRateCSV,
} from '@/api/demographicSurveyReport';
import { combineDeclinedAndUnknown, combineOtherAndUnknownCategories } from '@/stores/insights/helpers';
import { handleCSVDownload, arrayToCSVDownload } from '@/helpers/fileDownload';
import { sourceCategoryTitles } from '@/components/insights/ProcessInsights/DemographicSurveyReport/tables/helpers';
import { numberWithCommas } from '@/helpers/formatters';
import { useDatasetCounts } from './datasetCounts';

const STAGES = ['review', 'screen', 'assessment', 'offer', 'hired'];

const isSurveyInsightsEmpty = ({
  genderBreakdown,
  raceBreakdown,
  genderByStage,
  raceByStage,
}) => (isEmpty(genderBreakdown)
    || Object.values(genderBreakdown).every((item) => item === 0))
    && (isEmpty(raceBreakdown)
    || Object.values(raceBreakdown).every((item) => item === 0))
    && (isEmpty(genderByStage)
    || Object.values(genderByStage).every((item) => Object.values(item).every((i) => i === 0)))
    && (isEmpty(raceByStage)
    || Object.values(raceByStage).every((item) => Object.values(item).every((i) => i === 0)));

const isDistributionInsightsEmpty = ({
  reqFillRate,
  sourceFillRate,
}) => (isEmpty(reqFillRate?.items || []) || Object.values(reqFillRate).every((item) => item === 0))
    && (isEmpty(sourceFillRate) || Object.values(sourceFillRate).every((item) => item === 0));

export const useDemographicSurveyReport = defineStore('demographicSurveyReport', () => {
  // Other Stores
  const { addErrorSnackbar } = useAppSnackbar();

  // State
  const genderBreakdown = ref({});
  const raceBreakdown = ref({});
  const genderByStage = ref({});
  const raceByStage = ref({});
  const reqFillRate = ref({});
  const sourceFillRate = ref([]);
  const sourceOpenCategory = ref(null);
  const reqFillRateLoadingMore = ref(false);
  const reqFillRateLoading = ref(false);
  const reqFillRateSort = ref('fill_rate');
  const isSurveyInsightsTab = ref(true);
  const isTableGenderData = ref(true);

  // Computed
  const isEmptyTabData = computed(() => {
    if (isSurveyInsightsTab.value) {
      return isSurveyInsightsEmpty({
        genderBreakdown: genderBreakdown.value,
        raceBreakdown: raceBreakdown.value,
        genderByStage: genderByStage.value,
        raceByStage: raceByStage.value,
      });
    }

    return isDistributionInsightsEmpty({
      reqFillRate: reqFillRate.value,
      sourceFillRate: sourceFillRate.value,
    });
  });

  const isNoData = computed(() => isSurveyInsightsEmpty({
    genderBreakdown: genderBreakdown.value,
    raceBreakdown: raceBreakdown.value,
    genderByStage: genderByStage.value,
    raceByStage: raceByStage.value,
  }) && isDistributionInsightsEmpty({
    reqFillRate: reqFillRate.value,
    sourceFillRate: sourceFillRate.value,
  }));

  const genderTableData = computed(() => combineDeclinedAndUnknown(
    genderByStage.value,
    isTableGenderData.value,
  ));

  const raceTableData = computed(() => combineDeclinedAndUnknown(
    raceByStage.value,
    isTableGenderData.value,
  ));

  const genderAndRaceTableDatasetToUse = computed(() => {
    if (isTableGenderData.value) {
      return genderTableData.value;
    }

    return raceTableData.value;
  });

  const getDatasetTotals = (tableDataset) => {
    const finalTotals = {
      review: 0,
      screen: 0,
      assessment: 0,
      offer: 0,
      hired: 0,
    };

    Object.keys(tableDataset).forEach((group) => {
      STAGES.forEach((stage) => {
        finalTotals[stage] += tableDataset[group][stage];
      });
    });

    Object.keys(finalTotals).forEach((stage) => {
      finalTotals[stage] = {
        value: finalTotals[stage],
        percent: stage !== 'hired' && finalTotals[stage] !== 0
          ? ((finalTotals[STAGES[STAGES.indexOf(stage) + 1]]
            / finalTotals[stage]) * 100).toFixed(1)
          : null,
      };
    });

    return finalTotals;
  };

  const genderAndRaceTableTotals = computed(() => getDatasetTotals(
    genderAndRaceTableDatasetToUse.value,
  ));

  const labelMap = {
    male: 'Male',
    female: 'Female',
    genderUndefined: 'Undefined',
    asian: 'Asian',
    black: 'Black or African American',
    hispanic_latino: 'Hispanic or Latino',
    native_american: 'American Indian or Alaskan Native',
    two_or_more: 'Two or More Races',
    white: 'White',
    pacific_islander: 'Native Hawaiian or Pacific Islander',
    raceUndefined: 'Undefined',
  };

  const getRowsDataset = (dataset, isGenderData) => {
    const finalDatasets = [];

    Object.keys(dataset).forEach((key) => {
      const stageObj = {};

      Object.keys(dataset[key]).forEach((stage) => {
        stageObj[stage] = {
          value: dataset[key][stage],
          percent: stage !== 'hired' && dataset[key][stage] !== 0
            ? ((dataset[key][STAGES[STAGES.indexOf(stage) + 1]]
              / dataset[key][stage]) * 100).toFixed(1)
            : null,
        };
      });

      // Add the declined and not submitted rows as children to the Undefined row
      const declinedWithPercent = { label: 'Declined' };
      const unknownWithPercent = { label: 'Not Submitted' };

      STAGES.forEach((stage) => {
        declinedWithPercent[stage] = {
          value: isGenderData
            ? genderByStage.value.declined[stage]
            : raceByStage.value.declined[stage],
          percent: stage !== 'hired' && (isGenderData
            ? ((genderByStage.value.declined[STAGES[STAGES.indexOf(stage) + 1]]
              / genderByStage.value.declined[stage]) * 100).toFixed(1)
            : ((raceByStage.value.declined[STAGES[STAGES.indexOf(stage) + 1]]
              / raceByStage.value.declined[stage]) * 100).toFixed(1)),
        };

        unknownWithPercent[stage] = {
          value: isGenderData
            ? genderByStage.value.unknown[stage]
            : raceByStage.value.unknown[stage],
          percent: stage !== 'hired' && (isGenderData
            ? ((genderByStage.value.unknown[STAGES[STAGES.indexOf(stage) + 1]]
              / genderByStage.value.unknown[stage]) * 100).toFixed(1)
            : ((raceByStage.value.unknown[STAGES[STAGES.indexOf(stage) + 1]]
              / raceByStage.value.unknown[stage]) * 100).toFixed(1)),
        };
      });

      finalDatasets.push({
        label: labelMap[key],
        ...stageObj,
        children: key.endsWith('Undefined')
          ? [
            declinedWithPercent,
            unknownWithPercent,
          ].sort((a, b) => b.review.value - a.review.value)
          : [],
      });
    });

    return finalDatasets;
  };

  const genderAndRaceTableRows = computed(() => getRowsDataset(
    genderAndRaceTableDatasetToUse.value,
    isTableGenderData.value,
  ));

  const sortedSourceCategories = computed(() => combineOtherAndUnknownCategories(
    sourceFillRate.value,
  )
    .map((category) => {
      const sortedSources = category.sources.sort((a, b) => {
        if (b.fill_rate === a.fill_rate) {
          return b.total - a.total;
        }

        return a.fill_rate - b.fill_rate;
      });

      return { ...category, sources: sortedSources };
    })
    .sort((a, b) => {
      if (b.fill_rate === a.fill_rate) {
        return b.total - a.total;
      }

      return a.fill_rate - b.fill_rate;
    }));

  // Mutations
  const setData = ({
    newGenderBreakdown,
    newRaceBreakdown,
    newGenderByStage,
    newRaceByStage,
    newSourceFillRate,
  }) => {
    genderBreakdown.value = newGenderBreakdown;
    raceBreakdown.value = newRaceBreakdown;
    genderByStage.value = newGenderByStage;
    raceByStage.value = newRaceByStage;
    sourceFillRate.value = newSourceFillRate;
  };

  const updateSourceOpenCategory = (category) => {
    sourceOpenCategory.value = category;
  };

  const updateReqFillRateSort = ({ sort, sortDirection }) => {
    reqFillRateSort.value = sortDirection === 'asc' ? `-${sort}` : sort;
  };

  const updateIsSurveyInsightsTab = (value) => {
    isSurveyInsightsTab.value = value;
  };

  const updateIsTableGenderData = (value) => {
    isTableGenderData.value = value;
  };

  const calculateCandidatesForSummary = () => {
    const {
      male = 0,
      female = 0,
      declined = 0,
      unknown = 0,
    } = genderBreakdown.value;

    return numberWithCommas((male + female + declined + unknown) || 0);
  };

  // Actions
  const loadDemographicSurveyReport = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
  }) => {
    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      pageLoading: true, halfTopBarLoading: true,
    });

    const {
      genderBreakdown: newGenderBreakdown = {},
      raceBreakdown: newRaceBreakdown = {},
      genderByStage: newGenderByStage = {},
      raceByStage: newRaceByStage = {},
      sourceFillRate: newSourceFillRate = {},
    } = await getDemographicSurveyReport({
      selectedDateRange: date, routeQuery, filters: selectedFilters, selectedMyJobs,
    });

    setData({
      newGenderBreakdown,
      newRaceBreakdown,
      newGenderByStage,
      newRaceByStage,
      newSourceFillRate,
    });

    const total = calculateCandidatesForSummary();
    useDatasetCounts().setDatasetCounts(total);

    useAppLoading().setLoading({
      pageLoading: false, halfTopBarLoading: false,
    });
  };

  const fetchReqFillRate = async ({
    routeQuery, selectedDateRange, selectedFilters, selectedMyJobs,
  }) => {
    const { mergedTempFilters: filters } = storeToRefs(useFilters());
    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    reqFillRateLoading.value = true;

    try {
      const newReqFillRate = await loadReqFillRate({
        selectedDateRange: selectedDateRange || date,
        routeQuery,
        filters: selectedFilters || filters.value,
        sort: reqFillRateSort.value,
        page: 1,
        selectedMyJobs,
      });

      reqFillRate.value = newReqFillRate;
    } catch (error) {
      addErrorSnackbar({ text: `Update req fill rate sort - ${error.message}` });
      throw error;
    } finally {
      reqFillRateLoading.value = false;
    }
  };

  const loadMoreFillRateRequisitions = async ({ routeQuery, page }) => {
    const { pages } = reqFillRate.value || {};

    if (typeof page !== 'number' || typeof pages !== 'number' || page > pages) return;

    reqFillRateLoadingMore.value = true;

    try {
      const { mergedTempFilters: filters } = storeToRefs(useFilters());
      const { dateRange } = storeToRefs(useDatePicker());

      const newReqFillRate = await loadReqFillRate({
        selectedDateRange: dateRange.value,
        routeQuery,
        filters: filters.value,
        page,
        sort: reqFillRateSort.value,
      });

      reqFillRate.value = {
        ...newReqFillRate,
        items: reqFillRate.value.items.concat(newReqFillRate.items),
        page,
        sort: reqFillRateSort.value,
      };
    } catch (error) {
      addErrorSnackbar({ text: `Load more requisitions - ${error.message}` });
      throw error;
    } finally {
      reqFillRateLoadingMore.value = false;
    }
  };

  const downloadReqFillRateCSV = async ({
    routeQuery, projectName, dateRange,
  }) => {
    const { mergedTempFilters: filters, myJobsEnabled } = storeToRefs(useFilters());
    const lastQuarter = getQuarter(1);
    const fallbackDateRange = dateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    reqFillRateLoading.value = true;

    try {
      const commaSeparatedString = await downloadRequisitionFillRateCSV({
        selectedDateRange: dateRange || date,
        routeQuery,
        filters: filters.value,
        sort: reqFillRateSort.value,
        selectedMyJobs: myJobsEnabled.value,
      });

      const dateString = dateRange?.from && dateRange?.to
        ? `-${toFileNameFormat(dateRange.from)}-${toFileNameFormat(dateRange.to)}`
        : '';

      handleCSVDownload({
        commaSeparatedString,
        autoClick: true,
        title: `${kebabCase(projectName.toLowerCase())}-reporting-demographic-submission-by-requisition${dateString}.csv`,
      });
    } catch (error) {
      addErrorSnackbar({ text: `Requisition Fill Rate Download Error: ${error.message}` });
      throw error;
    } finally {
      reqFillRateLoading.value = false;
    }
  };

  const downloadSourceTableCSV = ({
    projectName, dateRange,
  }) => {
    const rows = [
      ['Source Category', 'Source Name', 'Submission Rate', 'Candidate Count'],
      ...sortedSourceCategories.value.map((row) => {
        const sourceCategoryLabel = sourceCategoryTitles[row.source_category];

        return [
          [sourceCategoryLabel, 'Category Total', `${row.fill_rate}%`, row.total],
          ...row.sources.map((source) => {
            const {
              ats_source_label: atsSourceLabel,
              total: totalApplications,
              fill_rate: fillRate,
            } = source;
            return [sourceCategoryLabel, atsSourceLabel, `${fillRate}%`, totalApplications];
          }),
        ];
      }).flat(),

    ];

    const dateString = dateRange?.from && dateRange?.to
      ? `-${toFileNameFormat(dateRange.from)}-${toFileNameFormat(dateRange.to)}`
      : '';

    arrayToCSVDownload({
      array: rows,
      title: `${kebabCase(projectName.toLowerCase())}-reporting-demographic-source${dateString}.csv`,
      autoClick: true,
    });
  };

  const downloadGenderOrRaceTableCSV = (projectName, dateRange, isGender) => {
    const dataToUse = isGender ? genderTableData.value : raceTableData.value;

    const tableTotals = getDatasetTotals(dataToUse);
    const rowsData = getRowsDataset(dataToUse, true);

    if (isEmpty(tableTotals) || isEmpty(rowsData)) return;

    const rows = [
      ['', 'Applied', 'Screening', 'Assessment', 'Offer', 'Hired'],
      ['Total', tableTotals.review.value, tableTotals.screen.value, tableTotals.assessment.value, tableTotals.offer.value, tableTotals.hired.value],
      ...rowsData.map((row) => [
        row.label,
        row.review.value,
        row.screen.value,
        row.assessment.value,
        row.offer.value,
        row.hired.value,
      ]),
    ];

    const dateString = dateRange?.from && dateRange?.to
      ? `-${toFileNameFormat(dateRange.from)}-${toFileNameFormat(dateRange.to)}`
      : '';

    arrayToCSVDownload({
      array: rows,
      title: `${kebabCase(projectName.toLowerCase())}-demographic-survey-report-${isGender ? 'gender' : 'race'}-data-table${dateString}.csv`,
      autoClick: true,
    });
  };

  const downloadGenderTableCSV = ({
    projectName, dateRange,
  }) => {
    downloadGenderOrRaceTableCSV(projectName, dateRange, true);
  };

  const downloadRaceTableCSV = ({
    projectName, dateRange,
  }) => {
    downloadGenderOrRaceTableCSV(projectName, dateRange, false);
  };

  const updateViaQueryParams = (query) => {
    if (query?.is_gender_table) isTableGenderData.value = query.is_gender_table === 'true';
    if (query?.source_open_category) sourceOpenCategory.value = query.source_open_category;
    if (query?.req_table_sort) reqFillRateSort.value = query.req_table_sort;
  };

  return {
    // state
    genderBreakdown,
    raceBreakdown,
    genderByStage,
    raceByStage,
    reqFillRate,
    sourceFillRate,
    sourceOpenCategory,
    reqFillRateLoadingMore,
    reqFillRateLoading,
    reqFillRateSort,
    isSurveyInsightsTab,
    isTableGenderData,
    // computed
    isEmptyTabData,
    isNoData,
    genderAndRaceTableTotals,
    genderAndRaceTableRows,
    sortedSourceCategories,
    // mutations
    setData,
    updateSourceOpenCategory,
    updateReqFillRateSort,
    updateIsSurveyInsightsTab,
    updateIsTableGenderData,
    // actions
    loadDemographicSurveyReport,
    loadMoreFillRateRequisitions,
    fetchReqFillRate,
    downloadReqFillRateCSV,
    downloadSourceTableCSV,
    downloadGenderOrRaceTableCSV,
    downloadGenderTableCSV,
    downloadRaceTableCSV,
    updateViaQueryParams,
  };
});
